<script setup lang="ts">
import { computed } from 'vue';
import GoldMedal from '../../assets/podium/Gold.svg';
import SilverMedal from '../../assets/podium/Silver.svg';
import BronzeMedal from '../../assets/podium/Bronze.svg';

export type PodiumPlayer = {
  position: number;
  playerId: string;
  score: number;
  avatar?: string;
};

type Props = {
  topPlayers: PodiumPlayer[];
  podiumsVisible: Set<number>;
  animatedScores: Map<number, number>;
  compact?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  compact: false,
});

// ==== PODIUM SIZE CONFIGURATIONS ====

const sizeConfig = {
  large: {
    container: {
      maxWidth: 600,
      minHeight: 300,
    },
    podiumHeights: {
      1: 250,
      2: 180,
      3: 120,
    },
    avatarSizes: {
      1: 100,
      2: 80,
      3: 70,
    },
  },
  small: {
    container: {
      maxWidth: 150,
      minHeight: 100,
    },
    podiumHeights: {
      1: 60,
      2: 50,
      3: 40,
    },
  },
};

// Podium colors
const podiumColors = {
  1: { color: 'amber', textColor: 'text-amber-darken-2' },
  2: { color: 'grey-lighten-1', textColor: 'text-grey-darken-2' },
  3: { color: 'brown-lighten-2', textColor: 'text-brown-darken-2' },
};

// Medal images
const medals = {
  1: GoldMedal,
  2: SilverMedal,
  3: BronzeMedal,
};

// ==== COMPUTED VALUES ====

const activeConfig = computed(() => (props.compact ? sizeConfig.small : sizeConfig.large));
const containerStyles = computed(() => ({
  maxWidth: `${activeConfig.value.container.maxWidth}px`,
  minHeight: `${activeConfig.value.container.minHeight}px`,
}));

const getPodiumHeight = (position: number) => {
  return activeConfig.value.podiumHeights[position as keyof typeof activeConfig.value.podiumHeights];
};

const getAvatarSize = (position: number) => {
  if (props.compact) return 40;
  return sizeConfig.large.avatarSizes[position as keyof typeof sizeConfig.large.avatarSizes];
};

const getPlayerByPosition = (position: number) => {
  return props.topPlayers.find((p) => p.position === position);
};

const isPodiumVisible = (position: number) => {
  return props.podiumsVisible.has(position);
};

const getAnimatedScore = (position: number) => {
  return props.animatedScores.get(position) || 0;
};
</script>

<template>
  <!-- Podium Layout: 2nd | 1st | 3rd -->
  <div
    class="podium-container d-flex align-end justify-center"
    :class="compact ? 'ga-2' : 'ga-4 ga-sm-8'"
    :style="containerStyles"
  >
    <div
      v-for="position in [2, 1, 3]"
      :key="position"
      v-show="getPlayerByPosition(position)"
      class="podium-column d-flex flex-column align-center"
    >
      <Transition name="podium-avatar">
        <div v-if="isPodiumVisible(position)" class="d-flex flex-column align-center mb-2">
          <v-avatar :size="getAvatarSize(position)" rounded="0" class="mb-1 podium-avatar">
            <v-img
              v-if="getPlayerByPosition(position)?.avatar"
              :src="getPlayerByPosition(position)!.avatar"
              :alt="`Player ${position}`"
            />
            <v-icon v-else color="white" icon="fa:fawsb fa-user"></v-icon>
          </v-avatar>
          <div class="chalkboard-font text-white text-body-1 font-weight-bold">
            {{ getAnimatedScore(position) }}
          </div>
        </div>
      </Transition>
      <div
        class="podium-bar rounded-t-lg d-flex align-center justify-center"
        :class="[`bg-${podiumColors[position as 1 | 2 | 3].color}`, { 'podium-visible': isPodiumVisible(position) }]"
        :style="{ '--podium-height': `${getPodiumHeight(position)}px` }"
      >
        <img
          v-if="!compact"
          :src="medals[position as 1 | 2 | 3]"
          :alt="`${position === 1 ? 'Gold' : position === 2 ? 'Silver' : 'Bronze'} Medal`"
          class="medal-image"
          :style="{
            width: position === 1 ? '96px' : position === 2 ? '80px' : '64px',
            height: position === 1 ? '96px' : position === 2 ? '80px' : '64px',
          }"
        />
        <v-chip
          v-else
          :size="position === 3 ? 'x-small' : position === 2 ? 'small' : undefined"
          :class="podiumColors[position as 1 | 2 | 3].textColor"
          class="chalkduster-font font-weight-bold"
        >
          {{ position }}
        </v-chip>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.podium-container {
  width: 100%;
  transition:
    max-width 0.6s ease,
    min-height 0.6s ease;
}

.podium-column {
  flex: 1;
  max-width: 180px;
}

.podium-bar {
  width: 100%;
  height: 0;
  transition: height 0.8s cubic-bezier(0.34, 1.56, 0.64, 1);
  overflow: hidden;

  &.podium-visible {
    height: var(--podium-height);
  }
}

// Avatar with smooth size transitions
.podium-avatar {
  transition:
    width 0.6s ease,
    height 0.6s ease;
}

// Avatar fade-in animation (initial reveal)
.podium-avatar-enter-active {
  transition: all 0.5s ease;
  transition-delay: 0.3s;
}

.podium-avatar-enter-from {
  opacity: 0;
  transform: translateY(-20px);
}

// Medal images
.medal-image {
  object-fit: contain;
  transition:
    width 0.3s ease,
    height 0.3s ease;
}
</style>
